import { clampFreeText } from './clamp-text.js';

/**
 * Preis an der Kasse korrigieren (U17g, Plan 16 Abschnitt 4.10.8). Entscheidung des Auftraggebers (04.10.): Jede Kassenkraft
 * darf mit Pflicht-Grund korrigieren, es gibt kein neues Recht. Alles ist protokolliert und für die Leitung sichtbar.
 *
 * Geld: ganze Cent, nie Fließkomma. `originalCents` ist der Preis, den die Kasse VOR der Korrektur zeigte (Listenpreis bzw.
 * Aktionspreis). Gezählt wird immer der korrigierte `priceCents` der Zeile, die Summe rechnet weiter `cartTotal`.
 */
export const PRICE_CHANGE_REASONS = [
  'defect',
  'label_differs',
  'part_missing',
  'seller_agreed',
  'other',
] as const;
export type PriceChangeReason = (typeof PRICE_CHANGE_REASONS)[number];

/** Längster Freitext zur Korrektur (Zeichen); für die Leitung, keine Personendaten. */
export const PRICE_CHANGE_NOTE_MAX = 200;

/** Zulässiger neuer Preis: 0,00 bis 5.000 € (ganze Cent). */
export const PRICE_CHANGE_MIN_CENTS = 0;
export const PRICE_CHANGE_MAX_CENTS = 500_000;

/** Klartext je Grund für Kasse, Leitung und Verkäufer. */
export const PRICE_CHANGE_REASON_LABELS: Readonly<Record<PriceChangeReason, string>> = {
  defect: 'Mangel oder Schaden',
  label_differs: 'Preis auf dem Etikett anders',
  part_missing: 'Teil fehlt',
  seller_agreed: 'Absprache mit dem Verkäufer',
  other: 'Anderer Grund',
};

/** Angabe an der Verkaufszeile (Protokoll): nur gesendet, wenn der Stand `capabilities.priceChangeReasons` meldet. */
export interface PriceChangePayload {
  originalCents: number;
  reason: PriceChangeReason;
  note?: string;
}

export function isPriceChangeReason(value: unknown): value is PriceChangeReason {
  return typeof value === 'string' && (PRICE_CHANGE_REASONS as readonly string[]).includes(value);
}

/** Gültiger neuer Preis einer Korrektur (ganze Cent im Bereich). */
export function isPriceChangeCents(value: number): boolean {
  return (
    Number.isInteger(value) && value >= PRICE_CHANGE_MIN_CENTS && value <= PRICE_CHANGE_MAX_CENTS
  );
}

/** Klartext für einen gespeicherten Grund; unbekannt ergibt „ohne Angabe“. */
export function priceChangeReasonLabel(reason: string | null | undefined): string {
  return isPriceChangeReason(reason) ? PRICE_CHANGE_REASON_LABELS[reason] : 'ohne Angabe';
}

/** Kürzt den Freitext auf `PRICE_CHANGE_NOTE_MAX` Zeichen; leer ergibt `undefined`. */
export function clampPriceChangeNote(text: string | undefined): string | undefined {
  return clampFreeText(text, PRICE_CHANGE_NOTE_MAX);
}

/**
 * Satz für den Verkäufer in „Meine Artikel“: Der Grund erscheint im Klartext, der freie Text der Kasse nie (er war an die
 * Leitung gerichtet). Beispiel: „Preis an der Kasse von 8,00 € auf 6,00 € korrigiert: Mangel oder Schaden“.
 */
export function priceChangeSentence(
  originalCents: number,
  priceCents: number,
  reason: string | null | undefined,
  format: (cents: number) => string,
): string {
  return `Preis an der Kasse von ${format(originalCents)} auf ${format(priceCents)} korrigiert: ${priceChangeReasonLabel(reason)}`;
}
